import { getCSRFToken } from '../utils/getCSRFToken';
import { processVerticalImageSmart } from '../utils/borderDetection';
import Pica from 'pica';
import Quill from 'quill';

export default function registerStorePages() {
  window.Alpine.store('storePages', {
    calendarSlug: '',
    path: '',
    items: [],
    allStorePages: [],
    filteredStorePages: [],
    admin_mode: true,
    addItemFormVisible: false,
    updateItemFormVisible: false,
    storePageSelected: null,
    imageProcessing: false,
    imageAdded: '',
    loading: true,
    isSubmitting: false,
    activeRequests: new Set(), // Track active requests to prevent duplicates
    toastStore: null, // Will be initialized after Alpine is ready
    addDescriptionQuill: null,
    updateDescriptionQuill: null,
    availableFeatures: [],
    availableRateSets: [],
    maxRelatedItems: 3,
    lastPageMode: 'classic',
    featureModalVisible: false,
    newFeature: {
      name: '',
      icon: 'fa-check',
    },
    formData: {
      id: '',
      title: '',
      short_description: '',
      description: '',
      initial_page_mode: 'classic',
      main_image: null,
      parent: '',
      page_mode: 'classic',
      use_primary_component_title_on_packages: false,
      listing_selector_heading: 'Choose your option',
      feature_ids: [],
      related_rateset_ids: [],
      related_store_page_ids: [],
      gallery_images: [],
      gallery_image_previews: [],
      gallery_main_selection: '',
      existing_gallery_images: [],
      remove_gallery_image_ids: [],
    },

    init() {
      // Initialize toastStore reference
      this.toastStore = Alpine.store('toastStore');
    },

    applyPageSquaresPayload(data) {
      if (!data) {
        return;
      }
      this.items = data.combined_items || [];
      this.allStorePages = data.all_store_pages || [];
      this.storePageSelected = data.selected_store_page_id;
      this.calendarSlug = data.calendar_selected;
      this.path = data.store_page_path;
    },

    hydrateFromServerElement(elementId) {
      const element = document.getElementById(elementId);
      if (!element || !element.textContent) {
        return false;
      }
      try {
        const data = JSON.parse(element.textContent);
        this.applyPageSquaresPayload(data);
        this.loading = false;
        return true;
      } catch (error) {
        console.error('Error parsing initial page squares payload:', error);
        return false;
      }
    },
    initDescriptionQuill(element, mode = 'add') {
      if (!element) return;

      const key =
        mode === 'update' ? 'updateDescriptionQuill' : 'addDescriptionQuill';
      const existing = this[key];
      if (
        existing &&
        existing.root &&
        existing.root.parentElement === element
      ) {
        existing.root.innerHTML = this.formData.description || '';
        return;
      }

      try {
        // Recreate instance for fresh x-if rendered nodes
        this[key] = new Quill(element, {
          theme: 'snow',
          modules: {
            toolbar: [
              [{ header: [4, 5, false] }],
              ['bold', 'italic', 'underline'],
              [{ list: 'ordered' }, { list: 'bullet' }],
            ],
          },
          formats: ['header', 'bold', 'italic', 'underline', 'list'],
        });
        this[key].root.innerHTML = this.formData.description || '';
        this[key].on('text-change', () => {
          this.formData.description = this[key].root.innerHTML;
        });
      } catch (error) {
        console.error(
          'Error initializing store page description editor:',
          error,
        );
      }
    },

    loadItems(url) {
      // Prevent duplicate requests
      if (this.activeRequests.has(url)) {
        console.log('Request already in progress for:', url);
        return;
      }

      this.loading = true;
      this.activeRequests.add(url);

      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          return response.json();
        })
        .then((data) => {
          this.applyPageSquaresPayload(data);
          console.log('this.allStorePages:', this.allStorePages);
        })
        .catch((error) => {
          console.error('Error loading items:', error);
          // Don't retry automatically to prevent loops
        })
        .finally(() => {
          this.loading = false;
          this.activeRequests.delete(url);
        });
    },

    safeLoadItems(url) {
      try {
        this.loadItems(url);
      } catch (error) {
        console.error('Error in store page initialization:', error);
        this.loading = false;
        this.activeRequests.delete(url);
      }
    },

    detectTransparency(canvas) {
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Check every 4th pixel (RGBA) for alpha channel
      // Skip some pixels for performance on large images
      const step = Math.max(
        1,
        Math.floor((canvas.width * canvas.height) / 10000),
      ); // Sample up to 10,000 pixels

      for (let i = 3; i < data.length; i += 4 * step) {
        if (data[i] < 255) {
          // Alpha channel less than 255 means transparency
          return true;
        }
      }

      return false;
    },

    fileToDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result || '');
        reader.onerror = () =>
          reject(new Error(`Failed to read file: ${file?.name || 'unknown'}`));
        reader.readAsDataURL(file);
      });
    },

    loadImage(dataUrl) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () =>
          reject(new Error('Failed to load image for processing'));
        img.src = dataUrl;
      });
    },

    canvasToBlob(canvas, format, quality) {
      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to encode image'));
              return;
            }
            resolve(blob);
          },
          format,
          quality,
        );
      });
    },

    async processStoreImageFile(file) {
      const pica = new Pica({
        features: ['js', 'wasm'], // Use JS and WASM but not web workers due to CSP restrictions
      });
      const maxSizeInBytes = 500 * 1024; // 500 KB
      const maxWidth = 1920; // Maximum width for resizing
      const maxHeight = 1080; // Maximum height for resizing
      const originalDataUrl = await this.fileToDataUrl(file);
      const img = await this.loadImage(originalDataUrl);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true });

      console.log(
        'Store page image processing - Dimensions:',
        img.width,
        'x',
        img.height,
      );

      const aspectRatio = img.width / img.height;
      const isTallerThan16_9 = aspectRatio < 16 / 9;

      if (isTallerThan16_9) {
        // Images taller than 16:9: Use smart processing for transparent/uniform border cases
        console.log(
          'Processing image taller than 16:9 with smart border detection',
        );
        processVerticalImageSmart(img, ctx, canvas);
      } else {
        // Square and Horizontal images: NO PROCESSING - use original
        console.log('Using original image - no processing needed');

        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0, img.width, img.height);
      }

      // Detect if image has transparency
      const hasTransparencyFlag = this.detectTransparency(canvas);

      // Choose format based on transparency
      const format = hasTransparencyFlag ? 'image/png' : 'image/jpeg';
      const quality = hasTransparencyFlag ? undefined : 0.9; // JPEG quality

      let blob = await this.canvasToBlob(canvas, format, quality);

      // Resize if the file size is greater than maxSizeInBytes or dimensions exceed maxWidth/maxHeight
      if (
        blob.size > maxSizeInBytes ||
        canvas.width > maxWidth ||
        canvas.height > maxHeight
      ) {
        const resizedCanvas = document.createElement('canvas');
        const width = canvas.width;
        const height = canvas.height;
        const scaleFactor = Math.min(
          maxWidth / width,
          maxHeight / height,
          Math.sqrt(maxSizeInBytes / blob.size),
        );
        resizedCanvas.width = width * scaleFactor;
        resizedCanvas.height = height * scaleFactor;

        await pica.resize(canvas, resizedCanvas);

        // Use the same format detection for resized image
        const resizedHasTransparencyFlag =
          this.detectTransparency(resizedCanvas);
        const resizedFormat = resizedHasTransparencyFlag
          ? 'image/png'
          : 'image/jpeg';
        const resizedQuality = resizedHasTransparencyFlag ? undefined : 0.9;

        blob = await this.canvasToBlob(
          resizedCanvas,
          resizedFormat,
          resizedQuality,
        );
      }

      // Create filename with correct extension based on format
      const extension = hasTransparencyFlag ? '.png' : '.jpg';
      const fileName = file.name.replace(/\.[^/.]+$/, '') + extension;
      const processedFile = new File([blob], fileName, {
        type: format,
      });

      return {
        file: processedFile,
        url: await this.fileToDataUrl(processedFile),
      };
    },

    async handleFileUpload(event) {
      const file = event.target.files[0];

      if (file) {
        this.imageProcessing = true;
        try {
          const processedImage = await this.processStoreImageFile(file);
          this.formData.main_image = processedImage.file;
          this.imageAdded = file.name;
        } catch (error) {
          console.error('Error processing store page main image:', error);
          alert(
            'Could not process the selected image. Please try another file.',
          );
        } finally {
          this.imageProcessing = false;
        }
      }
    },
    resetForm() {
      this.isSubmitting = false;
      this.lastPageMode = 'classic';
      (this.formData.gallery_image_previews || []).forEach((preview) => {
        if (preview?.url && String(preview.url).startsWith('blob:')) {
          URL.revokeObjectURL(preview.url);
        }
      });
      this.formData = {
        id: '',
        title: '',
        short_description: '',
        description: '',
        initial_page_mode: 'classic',
        main_image: null,
        parent: '',
        page_mode: 'classic',
        use_primary_component_title_on_packages: false,
        listing_selector_heading: 'Choose your option',
        feature_ids: [],
        related_rateset_ids: [],
        related_store_page_ids: [],
        gallery_images: [],
        gallery_image_previews: [],
        gallery_main_selection: '',
        existing_gallery_images: [],
        remove_gallery_image_ids: [],
      };
      // Reset all file inputs with the class 'main-image-file'
      const fileInputs = document.querySelectorAll('.main-image-file');
      fileInputs.forEach((input) => {
        input.value = '';
      });
      // Reset image added
      this.imageAdded = '';
      if (this.addDescriptionQuill) {
        this.addDescriptionQuill.root.innerHTML = '';
      }
      if (this.updateDescriptionQuill) {
        this.updateDescriptionQuill.root.innerHTML = '';
      }
    },
    async handleGalleryUpload(event) {
      this.formData.gallery_image_previews.forEach((preview) => {
        if (preview?.url && String(preview.url).startsWith('blob:')) {
          URL.revokeObjectURL(preview.url);
        }
      });
      const files = Array.from(event.target.files || []);
      if (!files.length) {
        this.formData.gallery_images = [];
        this.formData.gallery_image_previews = [];
        this.formData.gallery_main_selection = '';
        return;
      }

      this.imageProcessing = true;
      try {
        const processedImages = await Promise.all(
          files.map(async (file) => {
            try {
              return await this.processStoreImageFile(file);
            } catch (error) {
              console.error(
                `Error processing gallery image ${file.name}, using original file`,
                error,
              );
              return {
                file,
                url: await this.fileToDataUrl(file),
              };
            }
          }),
        );

        this.formData.gallery_images = processedImages.map(
          (processed) => processed.file,
        );
        this.formData.gallery_image_previews = processedImages.map(
          (processed) => ({
            file: processed.file,
            url: processed.url,
          }),
        );
      } finally {
        this.imageProcessing = false;
      }

      if (
        files.length > 0 &&
        (!this.formData.gallery_main_selection ||
          this.formData.gallery_main_selection.startsWith('new:'))
      ) {
        this.formData.gallery_main_selection = 'new:0';
      }
    },
    removeNewGalleryImage(index) {
      const preview = this.formData.gallery_image_previews[index];
      if (preview?.url && String(preview.url).startsWith('blob:')) {
        URL.revokeObjectURL(preview.url);
      }
      this.formData.gallery_image_previews.splice(index, 1);
      this.formData.gallery_images.splice(index, 1);
      if (this.formData.gallery_main_selection?.startsWith('new:')) {
        const selectedIndex = parseInt(
          this.formData.gallery_main_selection.split(':')[1] || '-1',
          10,
        );
        if (selectedIndex === index) {
          this.formData.gallery_main_selection =
            this.formData.gallery_images.length > 0 ? 'new:0' : '';
        } else if (selectedIndex > index) {
          this.formData.gallery_main_selection = `new:${selectedIndex - 1}`;
        }
      }
    },
    removeExistingGalleryImage(imageId) {
      this.formData.existing_gallery_images =
        this.formData.existing_gallery_images.filter(
          (image) => image.id !== imageId,
        );
      if (!this.formData.remove_gallery_image_ids.includes(imageId)) {
        this.formData.remove_gallery_image_ids.push(imageId);
      }
      if (this.formData.gallery_main_selection === `existing:${imageId}`) {
        this.formData.gallery_main_selection =
          this.formData.gallery_images.length > 0 ? 'new:0' : '';
      }
    },
    appendAdvancedFields(formData) {
      formData.append('page_mode', this.formData.page_mode || 'classic');
      formData.append(
        'use_primary_component_title_on_packages',
        this.formData.use_primary_component_title_on_packages
          ? 'true'
          : 'false',
      );
      formData.append(
        'listing_selector_heading',
        (this.formData.listing_selector_heading || 'Choose your option').trim(),
      );
      (this.formData.feature_ids || []).forEach((id) =>
        formData.append('feature_ids[]', id),
      );
      (this.formData.related_rateset_ids || []).forEach((id) =>
        formData.append('related_rateset_ids[]', id),
      );
      (this.formData.related_store_page_ids || []).forEach((id) =>
        formData.append('related_store_page_ids[]', id),
      );
      (this.formData.gallery_images || []).forEach((file) =>
        formData.append('gallery_images', file),
      );
      if (this.formData.gallery_main_selection) {
        formData.append(
          'gallery_main_selection',
          this.formData.gallery_main_selection,
        );
      }
      (this.formData.remove_gallery_image_ids || []).forEach((id) =>
        formData.append('remove_gallery_image_ids[]', id),
      );
    },
    stripHtml(text) {
      const temp = document.createElement('div');
      temp.innerHTML = text || '';
      return (temp.textContent || temp.innerText || '')
        .replace(/\s+/g, ' ')
        .trim();
    },
    onPageModeChanged(newMode) {
      const previousMode = this.lastPageMode || this.formData.page_mode;
      this.formData.page_mode = newMode;

      // Legacy classic sections used description as short text.
      // When switching to Single Page, migrate that value to short_description once.
      if (
        previousMode === 'classic' &&
        newMode === 'advanced' &&
        !this.formData.short_description &&
        this.formData.description
      ) {
        const migrated = this.stripHtml(this.formData.description);
        this.formData.short_description = migrated.slice(0, 200);
        this.formData.description = '';
        if (this.addDescriptionQuill) {
          this.addDescriptionQuill.root.innerHTML = '';
        }
        if (this.updateDescriptionQuill) {
          this.updateDescriptionQuill.root.innerHTML = '';
        }
      }

      this.lastPageMode = newMode;
    },
    getRelatedSelectedCount() {
      return (
        (this.formData.related_rateset_ids || []).length +
        (this.formData.related_store_page_ids || []).length
      );
    },
    isRelatedRateSetSelected(id) {
      return (this.formData.related_rateset_ids || []).includes(String(id));
    },
    isRelatedStorePageSelected(id) {
      return (this.formData.related_store_page_ids || []).includes(String(id));
    },
    toggleRelatedItem(type, id, checked) {
      const targetList =
        type === 'rateset'
          ? this.formData.related_rateset_ids
          : this.formData.related_store_page_ids;
      const normalizedId = String(id);
      const alreadySelected = targetList.includes(normalizedId);

      if (checked && !alreadySelected) {
        if (this.getRelatedSelectedCount() >= this.maxRelatedItems) {
          if (this.toastStore && this.toastStore.addToast) {
            this.toastStore.addToast(
              `You can select up to ${this.maxRelatedItems} related items.`,
              'warning',
            );
          }
          return false;
        }
        targetList.push(normalizedId);
        return true;
      }

      if (!checked && alreadySelected) {
        const idx = targetList.indexOf(normalizedId);
        if (idx !== -1) {
          targetList.splice(idx, 1);
        }
      }
      return true;
    },
    getFirstErrorMessage(payload, fallback = 'Could not save changes.') {
      if (!payload) return fallback;
      if (typeof payload.error === 'string' && payload.error.trim()) {
        return payload.error;
      }
      if (payload.errors && typeof payload.errors === 'object') {
        const firstKey = Object.keys(payload.errors)[0];
        const firstValue = payload.errors[firstKey];
        if (Array.isArray(firstValue) && firstValue.length > 0) {
          return String(firstValue[0]);
        }
        if (typeof firstValue === 'string' && firstValue.trim()) {
          return firstValue;
        }
      }
      return fallback;
    },
    showSubmitError(message) {
      if (this.toastStore && this.toastStore.addToast) {
        this.toastStore.addToast(message, 'warning');
      } else {
        alert(message);
      }
    },
    async fetchAdvancedOptions() {
      if (!this.calendarSlug || !this.storePageSelected) {
        return;
      }
      try {
        const response = await fetch(
          `/bookings/${this.calendarSlug}/${this.storePageSelected}/get_store_page_details/`,
        );
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        this.availableFeatures = data.features || [];
        this.availableRateSets = data.rate_sets || [];
      } catch (error) {
        console.error('Error fetching advanced options:', error);
      }
    },
    showFeatureModal(calendarSlug = null) {
      if (calendarSlug) {
        this.calendarSlug = calendarSlug;
      }
      this.featureModalVisible = true;
      this.newFeature = { name: '', icon: 'fa-check' };
      setTimeout(() => {
        const modal = document.querySelector('.store-pages-feature-modal');
        if (modal) {
          modal.style.display = 'block';
        }
      }, 50);
    },
    closeFeatureModal() {
      this.featureModalVisible = false;
      const modal = document.querySelector('.store-pages-feature-modal');
      if (modal) {
        modal.style.display = 'none';
      }
      const addFeatureBtn = document.querySelector(
        '[data-store-page-feature-add-btn]',
      );
      if (addFeatureBtn) addFeatureBtn.focus();
    },
    async submitFeature() {
      const effectiveCalendarSlug = this.calendarSlug;
      if (!effectiveCalendarSlug || !this.newFeature.name?.trim()) {
        this.showSubmitError('Please enter a feature name.');
        return;
      }
      try {
        const response = await fetch(
          `/bookings/${effectiveCalendarSlug}/create_feature/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({
              name: this.newFeature.name.trim(),
              icon: this.newFeature.icon || 'fa-check',
            }),
          },
        );
        const data = await response.json();
        if (!response.ok) {
          this.showSubmitError(
            this.getFirstErrorMessage(data, 'Could not create feature.'),
          );
          return;
        }
        this.availableFeatures.push({
          id: data.id,
          name: data.name,
          is_global: false,
          calendar_name: null,
        });
        this.closeFeatureModal();
      } catch (error) {
        console.error('Error creating feature:', error);
        this.showSubmitError('Could not create feature.');
      }
    },
    async submitForm(calendarSlug, parentPath) {
      if (this.isSubmitting) {
        return;
      }
      this.isSubmitting = true;
      if (!this.formData.title) {
        alert('Title is required');
        this.isSubmitting = false;
        return;
      }

      if (this.imageProcessing) {
        alert('Please wait until the image is processed.');
        this.isSubmitting = false;
        return;
      }

      const addUrl = `/bookings/${calendarSlug}/${parentPath}/add_store_page/`;

      const formData = new FormData();
      formData.append('title', this.formData.title);
      if (this.formData.short_description) {
        formData.append('short_description', this.formData.short_description);
      }
      if (this.formData.description) {
        formData.append('description', this.formData.description);
      }
      if (this.formData.main_image) {
        formData.append('main_image', this.formData.main_image);
      }
      this.appendAdvancedFields(formData);

      fetch(addUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRFToken': getCSRFToken(),
        },
      })
        .then(async (response) => {
          const data = await response.json();
          return { ok: response.ok, data };
        })
        .then(({ ok, data }) => {
          this.isSubmitting = false;
          if (!ok || data.errors) {
            console.error('Error adding item:', data.errors);
            this.showSubmitError(
              this.getFirstErrorMessage(
                data,
                'Could not create section. Please check your inputs.',
              ),
            );
          } else {
            this.items.unshift({ item: data, type: 'child' }); // Use unshift instead of push
            this.allStorePages.unshift(data); // Add new store page to allStorePages
            this.filteredStorePages = this.allStorePages.filter(
              (page) => page.id !== this.formData.id,
            );
            this.addItemFormVisible = false;
            if (this.toastStore && this.toastStore.addToast) {
              this.toastStore.addToast('New Section Added', 'success');
            }
            this.resetForm();
          }
        })
        .catch((error) => {
          this.isSubmitting = false;
          console.error('Error adding item:', error);
        });
    },
    async submitUpdateForm(calendarSlug) {
      if (this.isSubmitting) {
        return;
      }
      this.isSubmitting = true;
      if (!this.formData.title) {
        alert('Title is required');
        this.isSubmitting = false;
        return;
      }

      if (this.imageProcessing) {
        alert('Please wait until the image is processed.');
        this.isSubmitting = false;
        return;
      }

      const updateUrl = `/bookings/${calendarSlug}/${this.formData.id}/update_store_page/`;

      const formData = new FormData();
      formData.append('title', this.formData.title);
      if (this.formData.short_description) {
        formData.append('short_description', this.formData.short_description);
      }
      if (this.formData.description) {
        formData.append('description', this.formData.description);
      }

      // Debug logging for main_image
      console.log(
        'submitUpdateForm - main_image type:',
        typeof this.formData.main_image,
      );
      console.log(
        'submitUpdateForm - main_image value:',
        this.formData.main_image,
      );
      console.log(
        'submitUpdateForm - main_image instanceof File:',
        this.formData.main_image instanceof File,
      );

      if (this.formData.main_image) {
        // Ensure we're appending a File object, not an array
        if (this.formData.main_image instanceof File) {
          formData.append('main_image', this.formData.main_image);
        } else if (
          Array.isArray(this.formData.main_image) &&
          this.formData.main_image.length > 0
        ) {
          // If it's an array, take the first item
          console.warn('main_image is an array, taking first item');
          formData.append('main_image', this.formData.main_image[0]);
        } else {
          console.error(
            'main_image is not a File object:',
            this.formData.main_image,
          );
        }
      }
      formData.append('parent', this.formData.parent);
      this.appendAdvancedFields(formData);

      fetch(updateUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRFToken': getCSRFToken(),
        },
      })
        .then(async (response) => {
          let data = null;
          try {
            data = await response.json();
          } catch (error) {
            data = { error: 'Unexpected server response.' };
          }
          return { ok: response.ok, data, status: response.status };
        })
        .then(({ ok, data, status }) => {
          this.isSubmitting = false;
          if (!ok || data.errors) {
            console.error('Server response error:', status, data);
            console.error('Error updating item:', data.errors);
            this.showSubmitError(
              this.getFirstErrorMessage(
                data,
                'Could not update section. Please check your inputs.',
              ),
            );
          } else {
            const pageTypeElement = document.querySelector('.page-type');
            const pageType = pageTypeElement
              ? pageTypeElement.dataset.pageType
              : '';
            const changedCatalogToSinglePage =
              this.formData.initial_page_mode === 'classic' &&
              data.page_mode === 'advanced';
            const editingCurrentSection =
              String(this.storePageSelected || '') === String(data.id);
            if (
              pageType === 'store' &&
              changedCatalogToSinglePage &&
              editingCurrentSection
            ) {
              window.location.href = data.url || window.location.href;
              return;
            }
            if (pageType === 'store-advanced') {
              window.location.reload();
              return;
            }

            // Reload items to ensure everything is in sync
            if (this.calendarSlug && this.path) {
              const reloadUrl = `/bookings/${this.calendarSlug}/${this.path}/load_page_squares/`;
              this.safeLoadItems(reloadUrl);
            }

            // Update allStorePages
            const allStoreIndex = this.allStorePages.findIndex(
              (page) => page.id === data.id,
            );
            if (allStoreIndex !== -1) {
              this.allStorePages.splice(allStoreIndex, 1, data);
            }
            this.filteredStorePages = this.allStorePages.filter(
              (page) => page.id !== this.formData.id,
            );

            this.updateItemFormVisible = false;
            if (this.toastStore && this.toastStore.addToast) {
              this.toastStore.addToast('Section Updated', 'success');
            }
            this.resetForm();
          }
        })
        .catch((error) => {
          this.isSubmitting = false;
          console.error('Error updating item:', error);
        });
    },
    async showUpdateForm(storePageId, calendarSlug = null) {
      const effectiveCalendarSlug = calendarSlug || this.calendarSlug;
      if (!effectiveCalendarSlug) {
        console.error(
          'Cannot open store page editor: missing calendar slug',
          storePageId,
        );
        return;
      }
      this.calendarSlug = effectiveCalendarSlug;

      const storePageFromItems = this.items.find(
        (item) => item?.item?.id === storePageId,
      );
      const storePageFromAll = this.allStorePages.find(
        (page) => page?.id === storePageId,
      );

      const response = await fetch(
        `/bookings/${effectiveCalendarSlug}/${storePageId}/get_store_page_details/`,
      );
      if (!response.ok) {
        console.error('Failed to fetch store page details:', response.status);
        return;
      }

      const data = await response.json();
      const details = data.store_page || {};
      const fallbackPage = storePageFromItems?.item || storePageFromAll || {};
      const existingMainImage = (details.images || []).find(
        (image) => image.is_main,
      );

      this.availableFeatures = data.features || [];
      this.availableRateSets = data.rate_sets || [];
      this.formData = {
        id: details.id || fallbackPage.id || storePageId,
        title: details.title || fallbackPage.title || '',
        short_description:
          details.short_description || fallbackPage.short_description || '',
        description: details.description || fallbackPage.description || '',
        initial_page_mode:
          details.page_mode || fallbackPage.page_mode || 'classic',
        main_image: null,
        parent:
          details.parent !== undefined
            ? details.parent
            : fallbackPage.parent ?? '',
        page_mode: details.page_mode || fallbackPage.page_mode || 'classic',
        use_primary_component_title_on_packages:
          details.use_primary_component_title_on_packages !== undefined
            ? details.use_primary_component_title_on_packages
            : fallbackPage.use_primary_component_title_on_packages ?? false,
        listing_selector_heading:
          details.listing_selector_heading ||
          fallbackPage.listing_selector_heading ||
          'Choose your option',
        feature_ids: details.feature_ids || [],
        related_rateset_ids: (details.related_rateset_ids || []).map(String),
        related_store_page_ids: (details.related_store_page_ids || []).map(
          String,
        ),
        gallery_images: [],
        gallery_image_previews: [],
        gallery_main_selection: existingMainImage
          ? `existing:${existingMainImage.id}`
          : details.images && details.images.length > 0
          ? `existing:${details.images[0].id}`
          : '',
        existing_gallery_images: details.images || [],
        remove_gallery_image_ids: [],
      };
      this.lastPageMode = this.formData.page_mode || 'classic';
      if (this.updateDescriptionQuill) {
        this.updateDescriptionQuill.root.innerHTML =
          this.formData.description || '';
      }
      this.filteredStorePages = data.store_pages || [];
      this.updateItemFormVisible = true;
    },
    deleteStorePage(storePageId) {
      if (
        confirm(
          'Are you sure you want to delete this Section? All Listings inside Section will be moved to this parent Section.',
        )
      ) {
        const deleteUrl = `/bookings/${this.calendarSlug}/${storePageId}/delete_store_page/`;

        fetch(deleteUrl, {
          method: 'DELETE',
          headers: {
            'X-CSRFToken': getCSRFToken(),
          },
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.success) {
              this.items = data.combined_items;
              this.updateItemFormVisible = false;
              this.resetForm();
              if (this.toastStore && this.toastStore.addToast) {
                this.toastStore.addToast('Section Deleted', 'danger');

                if (data.reassigned_items && data.reassigned_items.length > 0) {
                  const reassignedItemsList = data.reassigned_items.join(', ');
                  this.toastStore.addToast(
                    `The following Sections and Listings have been moved to this Section: ${reassignedItemsList}`,
                    'info',
                  );
                }
              }
            } else {
              console.error('Error deleting item:', data.error);
            }
          })
          .catch((error) => console.error('Error deleting item:', error));
      }
    },
    setAdminMode(value) {
      this.admin_mode = value;
    },
    async initSortable() {
      const { default: Sortable } = await import('sortablejs');
      const el = document.getElementById('page-squares-container');
      const self = this; // capture 'this' context
      Sortable.create(el, {
        handle: '.drag-handle', // Only allow drag using the handle
        onEnd: function (evt) {
          const order = Array.from(el.children).map((child) => ({
            id: child.getAttribute('data-id'),
            type: child.getAttribute('data-type'),
          }));
          self.updateOrder(order); // use 'self' to call 'updateOrder'
        },
      });
    },
    updateOrder(order) {
      const updateUrl = `/bookings/${this.calendarSlug}/${this.path}/update/order/`;

      fetch(updateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
        body: JSON.stringify({ order: order }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            console.log('Order updated successfully');
          } else {
            console.error('Error updating order:', data.error);
          }
        })
        .catch((error) => console.error('Error updating order:', error));
    },
  });
}
